/** @format */
import {
    PaymentGatewayType,
    PaymentMethodType,
    TransactionVariant,
} from '../constants';
import {
    type Address,
    type AddressWithId,
    type TransactionSummaryAddress,
} from './address';
import { type Adjustment } from './adjustment';
import { type Attribute, AttributeKey } from './attribute';
import {
    type CustomerDetails,
    type TransactionByCustomerResponse,
} from './customer';
import {
    type DeliverySummary,
    DeliveryType,
    PublicConsignmentState,
} from './delivery';
import { LineItemState } from './line-item';
import { type ItemObservation } from './observation';
import { DeclineReason, type PriceModifier } from './price-modifier';
import { type ProductItemSummary } from './product';
import { type PromotionSummary } from './promotion';
import {
    PublicQuoteItemState,
    PublicTransactionQuoteState,
    type QuoteDeliveryOption,
    type QuoteItemSummary,
    type QuoteSummary,
    QuoteSummaryState,
} from './quote';
import { type TaxSummary } from './tax-item';
import { type ProductItemWarrantySummary } from './warranty';

export {
    type Attribute,
    AttributeKey,
    type Address,
    type AddressWithId,
    type CustomerDetails,
    type DeliverySummary,
    LineItemState,
    type ProductItemSummary,
    type PromotionSummary,
    PublicTransactionQuoteState,
    type QuoteItemSummary,
    type TaxSummary,
    type ProductItemWarrantySummary,
    type QuoteDeliveryOption,
    type QuoteSummary,
    QuoteSummaryState,
    DeliveryType,
    type TransactionSummaryAddress,
    PublicConsignmentState,
    type TransactionByCustomerResponse,
    type Adjustment,
    type ItemObservation,
    PublicQuoteItemState,
    type PriceModifier,
    DeclineReason,
};

export enum ItemType {
    ITEMS_TO_BUY = 'itemsToBuy',
    ITEMS_TO_SELL = 'itemsToSell',
    ITEMS_TO_RETURN = 'itemsToReturn',
    UNLISTED_ITEMS = 'unlistedItems',
}

export enum TransactionState {
    UNDEFINED = 'UNDEFINED',
    ADJUSTED_QUOTE = 'ADJUSTED_QUOTE',
    ADJUSTED_QUOTE_SENT = 'ADJUSTED_QUOTE_SENT',
    AWAITING_CHECKING = 'AWAITING_CHECKING',
    AWAITING_DELIVERY_CONFIG = 'AWAITING_DELIVERY_CONFIG',
    AWAITING_UNBOXING = 'AWAITING_UNBOXING',
    AWAITING_COURIER_BOOKING = 'AWAITING_COURIER_BOOKING',
    AWAITING_CUSTOMER_VERIFICATION = 'AWAITING_CUSTOMER_VERIFICATION',
    AWAITING_PAYMENT = 'AWAITING_PAYMENT',
    AWAITING_PAYMENT_RELEASE = 'AWAITING_PAYMENT_RELEASE',
    AWAITING_REPAIR = 'AWAITING_REPAIR',
    AWAITING_BATCH_CONFIRMATION = 'AWAITING_BATCH_CONFIRMATION',
    AWAITING_RESOLUTION = 'AWAITING_RESOLUTION',
    BUILDING_QUOTE_REQUEST = 'BUILDING_QUOTE_REQUEST',
    CANCELLED = 'CANCELLED',
    COMPLETE = 'COMPLETE',
    COURIER_BOOKED = 'COURIER_BOOKED',
    OK_TO_PAY = 'OK_TO_PAY',
    PACKED = 'PACKED',
    PICKABLE = 'PICKABLE',
    QUOTE_MODIFIED = 'QUOTE_MODIFIED',
    QUOTE_SENT = 'QUOTE_SENT',
    SHIPPED = 'SHIPPED',
    UNPICKABLE = 'UNPICKABLE',
    DECLINED = 'DECLINED',
    PACKABLE = 'PACKABLE',
}

export enum PublicTransactionStage {
    UNDEFINED_STAGE = 'UNDEFINED_STAGE',
    QUOTE_CREATED = 'QUOTE_CREATED',
    SHIPPING_BOOKED = 'SHIPPING_BOOKED',
    TRANSACTION_RECEIVED = 'TRANSACTION_RECEIVED',
    QUOTE_ADJUSTED = 'QUOTE_ADJUSTED',
    READY_TO_PAY = 'READY_TO_PAY',
    PAID = 'PAID',
    TRANSACTION_CREATED = 'TRANSACTION_CREATED',
    TRANSACTION_SHIPPED = 'TRANSACTION_SHIPPED',
    CONFIGURE_SHIPPING = 'CONFIGURE_SHIPPING',
    AWAITING_PAYMENT_DETAILS = 'AWAITING_PAYMENT_DETAILS',
    AWAITING_PAYMENT = 'AWAITING_PAYMENT',
    PAYMENT_PENDING = 'PAYMENT_PENDING',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface TransactionQuoteSummary {
    id: string;
    expiryTime: string | null;
    sentTime: string | null;
    promoCode: string;
    state: PublicTransactionQuoteState;
    items: QuoteItemSummary[];
    /**
     * If the quote is expired. NOTE: This is added on the FE
     */
    expired?: boolean;
}

export type TaxTotalKeys =
    | 'balance'
    | 'totalExTax'
    | 'totalIncTax'
    | 'totalPayments'
    | 'totalTax';

export type TransactionTotals = Record<TaxTotalKeys, number>;
export type FormattedTransactionTotals = Record<TaxTotalKeys, string>;

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface TransactionReference {
    id: string;
    reference: string;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface TransactionRelationship {
    root?: TransactionReference;
    parent?: TransactionReference;
    children?: TransactionReference[];
}

export enum DisputeItemType {
    GOODS_OUT = 'GOODS_OUT',
    CHARGE = 'CHARGE',
    WARRANTY = 'WARRANTY',
}

export type DisputeItem = {
    id: string;
    disputedItemType: DisputeItemType;
    value: number;
    taxInclusive: boolean;
    state: LineItemState;
    description: string;
    relatedProductSku: string;
};

export enum TransactionKind {
    TRANSACTION_SUMMARY = 'TRANSACTION_SUMMARY',
    BASKET_TRANSACTION_SUMMARY = 'BASKET_TRANSACTION_SUMMARY',
    CHECKOUT_TRANSACTION_SUMMARY = 'CHECKOUT_TRANSACTION_SUMMARY',
}

export type TransactionSummary = {
    kind: TransactionKind.TRANSACTION_SUMMARY;
    id: string;
    market: string;
    reference: string;
    customerDetails: CustomerDetails;
    customerId: number;
    initialQuote?: TransactionQuoteSummary;
    quote?: TransactionQuoteSummary;
    billingAddress?: Address;
    state: TransactionState;
    stage: PublicTransactionStage;
    disputes: DisputeItem[];
    itemsToSell: ProductItemSummary[];
    itemsToBuy: ProductItemSummary[];
    itemsToReturn: ProductItemSummary[];
    deliveries: DeliverySummary[];
    collections: DeliverySummary[];
    shipments: DeliverySummary[];
    promotions: PromotionSummary[];
    totals: TransactionTotals;
    variant?: TransactionVariant;
    createdDate: string;
    modifiedDate: string;
    relationship?: TransactionRelationship;
    taxExemptionReference?: string;
    totalActivePayments?: number;
    tax: TaxSummary[];
    checkout?: CheckoutTransactionSummary;
};

export type BasketTransactionSummary = {
    kind: TransactionKind.BASKET_TRANSACTION_SUMMARY;
    market: string;
    reference?: string;
    customerDetails: CustomerDetails;
    itemsToBuy: ProductItemSummary[];
    promotions: PromotionSummary[];
    totals?: TransactionTotals;
    deliveries?: DeliverySummary[];
    variant?: TransactionVariant;
};

export type CheckoutTransactionSummary = {
    kind: TransactionKind.CHECKOUT_TRANSACTION_SUMMARY;
    market: string;
    reference?: string;
    customerDetails: CustomerDetails;
    itemsToBuy: ProductItemSummary[];
    promotions: PromotionSummary[];
    totals?: TransactionTotals;
    deliveries?: DeliverySummary[];
    variant?: TransactionVariant;
    checkoutId: string;
    itemsToSell: [];
    payment?: Omit<CheckoutPayment, 'billingAddress'> & {
        billingAddress: Address;
    };
    warranties?: CheckoutWarranty[];
    transactionId: TransactionSummary['id'];
};

export type MixedTransaction =
    | TransactionSummary
    | BasketTransactionSummary
    | CheckoutTransactionSummary;

export type CheckoutPayment = {
    id: number;
    amount: string;
    billingAddress: CheckoutAddress;
    deviceData?: string;
    gatewayData: {
        gateway: PaymentGatewayType;
        method: PaymentMethodType;
        paymentAmount: string;
        token: string;
    };
};

export type CheckoutAddress = {
    addressId: number;
    addressLine1: string;
    addressLine2: string;
    addressee: string;
    country: string;
    county: string;
    postcode: string;
    town: string;
    verified: boolean;
    billingAddress: boolean;
    deliveryAddress: boolean;
};

export type CheckoutWarranty = {
    productId: number;
    selectedPolicies: { policyId: string }[];
    warrantyContextId: string;
};
